import { useState } from 'react';
import type { FilterGroup } from '@/types';

interface FilterBarProps {
  groups: FilterGroup[];
  selected: Record<string, string>;
  onSelect: (key: string, value: string) => void;
  sort: string;
  onSort: (value: string) => void;
  onReset: () => void;
}

const sortOptions = [
  { key: 'time', label: '最新发布', icon: 'ri-time-line' },
  { key: 'hot', label: '最多热度', icon: 'ri-fire-line' },
];

function FilterGroups({ groups, selected, onSelect }: Pick<FilterBarProps, 'groups' | 'selected' | 'onSelect'>) {
  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <div key={group.key} className="flex flex-col gap-2 sm:flex-row sm:items-start">
          <span className="shrink-0 pt-1.5 text-sm font-medium text-foreground-500 sm:w-16">{group.label}</span>
          <div className="flex flex-wrap gap-2">
            {group.options.map((opt) => {
              const active = (selected[group.key] || '全部') === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => onSelect(group.key, opt)}
                  className={`rounded-md px-3 py-1.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    active
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700'
                  }`}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function FilterBar({ groups, selected, onSelect, sort, onSort, onReset }: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const activeCount = Object.entries(selected).filter(([, v]) => v && v !== '全部').length;

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="hidden md:block">
        <FilterGroups groups={groups} selected={selected} onSelect={onSelect} />
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4">
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 text-sm font-medium text-foreground-500">
              <i className="ri-sort-desc"></i>
              排序
            </span>
            {sortOptions.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => onSort(s.key)}
                className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm transition-colors cursor-pointer ${
                  sort === s.key
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-secondary-100'
                }`}
              >
                <i className={s.icon}></i>
                {s.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1 text-sm text-foreground-500 transition-colors hover:text-primary-600 cursor-pointer"
          >
            <i className="ri-refresh-line"></i>
            重置筛选
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 md:hidden">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-background-100 px-3 py-2.5 text-sm font-medium text-foreground-700 cursor-pointer"
        >
          <i className="ri-filter-3-line"></i>
          筛选
          {activeCount > 0 && (
            <span className="rounded-full bg-primary-500 px-1.5 text-xs text-background-50">{activeCount}</span>
          )}
        </button>
        <button
          type="button"
          onClick={() => onSort(sort === 'time' ? 'hot' : 'time')}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-background-100 px-3 py-2.5 text-sm font-medium text-foreground-700 cursor-pointer"
        >
          <i className="ri-sort-desc"></i>
          {sort === 'time' ? '最新' : '最热'}
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)}></div>
          <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto rounded-t-lg bg-background-50 p-5 animate-fade-rise">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground-900">筛选与排序</h3>
              <button type="button" onClick={() => setOpen(false)} className="rounded-md p-2 text-foreground-500 cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <FilterGroups groups={groups} selected={selected} onSelect={onSelect} />
            <div className="mt-5 flex items-center gap-2 border-t border-background-200 pt-4">
              <span className="text-sm font-medium text-foreground-500">排序</span>
              {sortOptions.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => onSort(s.key)}
                  className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm cursor-pointer ${
                    sort === s.key ? 'bg-secondary-500 text-background-50' : 'bg-background-100 text-foreground-700'
                  }`}
                >
                  <i className={s.icon}></i>
                  {s.label}
                </button>
              ))}
            </div>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={onReset}
                className="flex-1 rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 cursor-pointer"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex-1 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 cursor-pointer"
              >
                查看结果
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}